/**
 * The landing page is modal (DESIGN §10): what it shows is a function of the
 * release phase derived from the observed documents, not of navigation. Idle → the
 * unreleased summary and — only when there is something to release — the "start a
 * release" action; preparing → the guided preparation flow; deploying → the
 * component dashboard. Three views, one page, and which one an operator gets is not
 * theirs to choose: the release is in one state, and the page shows that state's
 * work.
 *
 * Ahead of all three sits one question the flow asks before it shows anything: is
 * the release system awake? The VMs it runs on are an implementation detail of a
 * tool that exists to guide someone through a release, and knowing which machine
 * each phase needed — and pressing Start on the right card at the right moment —
 * was the most confusing thing about using it. So a system that is not up replaces
 * the page with one alert and one button (`SystemSleepingPanel`), the flow itself
 * never mentions the machines, and the only thing it says about them afterwards is
 * how long they have got (`SystemStatusBar`). The per-machine cards, health checks
 * and instruction history live on `/hosts/:role`, where troubleshooting belongs.
 */
import { Alert, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { INSTRUCTION_KIND, isInflightStatus } from '@finvari/deployer';
import { ActionCard } from '../components/ActionCard';
import { DeployingView } from '../components/DeployingView';
import { PreparingView } from '../components/PreparingView';
import { SystemSleepingPanel } from '../components/SystemSleepingPanel';
import { SystemStatusBar } from '../components/SystemStatusBar';
import { UnreleasedCard } from '../components/UnreleasedCard';
import {
    useHost,
    useNow,
    useRecentInstructions,
    useReleasePosition,
    useReleaseSystem,
    useUnreleased,
    CAPTION_TICK_MS,
} from '../hooks';
import { showsDeployingView, showsLoadingView, showsPreparingView } from '../lib/homeMode';
import { isAwake } from '../lib/releaseSystem';
import { unreleasedView } from '../lib/unreleased';
import { latestByKind } from '../lib/instructionActivity';

export function HomePage() {
    const prepareHost = useHost('prepare');
    const position = useReleasePosition();
    const { phase, prepare: active, deploy } = position;
    const system = useReleaseSystem(phase);
    const recent = useRecentInstructions('prepare').value;
    const unreleased = unreleasedView(useUnreleased().value, prepareHost.value, useNow(CAPTION_TICK_MS));

    // The page only leaves idle once the prepare host has written the release
    // document, which happens when it *claims* `prepare.start` — so while that
    // instruction is queued (system asleep, or not yet picked up) the page is
    // still idle and must say so, or the operator reads "no release" as "my
    // click was lost".
    const startRequested = latestByKind(recent).get(INSTRUCTION_KIND.prepareStart);
    const startInflight = !!startRequested && isInflightStatus(startRequested.status);

    // Which half of the page to show is a function of documents that have not
    // arrived yet, and the idle half is what an empty read looks like. Rendering it
    // early tells an operator with a release open that there is none, complete with
    // a button to start another one, so the page waits for the sources that decide
    // the mode rather than guessing at them. The system's own state is one of those
    // sources: an unread host document is what "asleep" looks like too, and a wake
    // button for a system that is already up is the same wrong click.
    if (showsLoadingView(position.loading, system)) {
        return (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                    Reading the release state…
                </Typography>
            </Stack>
        );
    }

    const preparing = !!active && showsPreparingView(active, phase);
    const deploying = !!position.version && showsDeployingView(phase);

    // Nothing in any of the three views can run on a sleeping system — the legality
    // table refuses every action — so the flow is replaced rather than disabled. A
    // page of dead buttons under a wake button is what sent v1.1266's operator
    // looking for a tool that had lost track of its own release.
    if (!isAwake(system)) {
        return (
            <SystemSleepingPanel
                system={system}
                heading={
                    preparing && active
                        ? `Preparing ${active.version}`
                        : deploying
                          ? `Deploying ${position.version}`
                          : 'Release control'
                }
                waitingOn={waitingOnText({
                    preparing,
                    deploying,
                    version: position.version,
                    startInflight,
                    nextVersion: unreleased.nextVersion,
                })}
            />
        );
    }

    return (
        <Stack spacing={3}>
            {preparing && active ? (
                <PreparingView release={active} phase={phase} />
            ) : deploying && position.version ? (
                // `position.version` comes from whichever half has a document: in
                // `release_host_starting` reached from preparation only the prepare
                // document exists, and on a rebuilt prepare host only the `(default)`
                // one does. A deploying phase implies at least one of them, since
                // `published` is true only when the prepare document's publish step is
                // done or a `(default)` document exists — so the guard never sends a
                // published release to the idle view.
                <DeployingView version={position.version} release={deploy} phase={phase} />
            ) : (
                <IdleView startInflight={startInflight} canStart={unreleased.canStart} nextVersion={unreleased.nextVersion} />
            )}

            <SystemStatusBar system={system} />
        </Stack>
    );
}

/**
 * What the wake is holding up, in the operator's terms. The sleeping panel replaces
 * the flow, so the release it replaced has to be named: an operator who left a
 * preparation running an hour ago and comes back to "the system is asleep" needs to
 * see that the release is still there before they will believe the button.
 */
function waitingOnText(input: {
    preparing: boolean;
    deploying: boolean;
    version: string | undefined;
    startInflight: boolean;
    nextVersion: string | undefined;
}): string {
    if (input.preparing) return `${input.version} is part-way through preparation, and the rest of it runs on the system.`;
    if (input.deploying) return `${input.version} is published and waiting to be deployed.`;
    // A queued start is the one idle case with something already pending: the click
    // landed, the instruction is waiting, and the wake is what makes it run.
    if (input.startInflight) {
        return input.nextVersion
            ? `Preparing ${input.nextVersion} has been requested and starts as soon as the system is awake.`
            : 'A preparation has been requested and starts as soon as the system is awake.';
    }
    return 'Checking what is unreleased and preparing a release both run on the system, so waking it is the first step.';
}

/**
 * The idle flow, ordered by what the operator came to decide: the answer to "is
 * there anything to release?" first, then the decision it unlocks.
 */
function IdleView({
    startInflight,
    canStart,
    nextVersion,
}: {
    startInflight: boolean;
    canStart: boolean;
    nextVersion: string | undefined;
}) {
    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h5" component="h1">
                        Release control
                    </Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={startInflight ? 'primary' : 'default'}
                        label={startInflight ? 'Preparation requested' : 'No release in progress'}
                    />
                </Stack>
                {startInflight ? (
                    <Alert severity="info">
                        Preparation has been requested. This page switches to the preparation view as soon as it is
                        picked up — the card below shows where it stands.
                    </Alert>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
                        Preparation merges main into the release branch, captures the PR diffs, and drafts the
                        announcement for your review. Nothing is published until you approve it.
                        {canStart && ' Start one when now is a good time and the people who need to watch it are around.'}
                    </Typography>
                )}
            </Stack>

            <UnreleasedCard />

            {/*
              * The start action appears only once a check has found something to
              * release. Preparing a release with nothing in it produces an empty
              * version — a tag, notes and an announcement about no changes — and
              * the way out of it is an abort, so the button is not offered until
              * there is a reason to press it. A start already in flight keeps its
              * card either way: the alert above points at it, and a queued click
              * that vanishes reads as a lost click.
              */}
            {(canStart || startInflight) && (
                <ActionCard
                    host="prepare"
                    kind={INSTRUCTION_KIND.prepareStart}
                    title={nextVersion ? `Start preparing ${nextVersion}` : undefined}
                    emphasis
                />
            )}
        </Stack>
    );
}
